import {
  ADJACENT_OFFSETS,
  BOARD_SIZE,
  MAX_STACK_HEIGHT,
  cellIndex,
  compareCoordinates,
  coordinateAt,
  forwardRowDelta,
  isOnBoard,
  progressAt,
} from "./geometry";
import {
  coordinate,
  fieldCell,
  opponentOf,
  type Cell,
  type Coordinate,
  type GameState,
  type Move,
  type Player,
  type RemovedOrphan,
} from "./model";

export function cellAt(state: GameState, position: Coordinate): Cell | null {
  return isOnBoard(position) ? (state.cells[cellIndex(position)] ?? null) : null;
}

export function getFrontier(state: GameState, player: Player): number | null {
  const opponent = opponentOf(player);
  let frontier: number | null = null;

  for (let index = 0; index < state.cells.length; index += 1) {
    const cell = state.cells[index];
    if (cell?.kind !== "field" || !cell.stack.includes(opponent)) continue;
    const progress = progressAt(player, coordinateAt(index));
    frontier = frontier === null ? progress : Math.max(frontier, progress);
  }

  return frontier;
}

function survivesSourceDeparture(
  cells: readonly Cell[],
  position: Coordinate,
  from: Coordinate,
): boolean {
  return ADJACENT_OFFSETS.some((offset) => {
    const neighbor = coordinate(position.row + offset.row, position.column + offset.column);
    return (
      isOnBoard(neighbor) &&
      (neighbor.row !== from.row || neighbor.column !== from.column) &&
      cells[cellIndex(neighbor)]?.kind === "field"
    );
  });
}

function getDepartureFrontier(
  state: GameState,
  player: Player,
  from: Coordinate,
  currentFrontier: number | null = getFrontier(state, player),
): number | null {
  const source = isOnBoard(from) ? state.cells[cellIndex(from)] : undefined;
  if (source?.kind !== "field" || source.stack.length !== 1) return currentFrontier;

  const opponent = opponentOf(player);
  let survivingFrontier: number | null = null;
  for (let index = 0; index < state.cells.length; index += 1) {
    const cell = state.cells[index];
    if (cell?.kind !== "field" || !cell.stack.includes(opponent)) continue;
    const position = coordinateAt(index);
    if (!survivesSourceDeparture(state.cells, position, from)) continue;
    const progress = progressAt(player, position);
    survivingFrontier =
      survivingFrontier === null ? progress : Math.max(survivingFrontier, progress);
    if (survivingFrontier === currentFrontier) return currentFrontier;
  }
  return survivingFrontier ?? currentFrontier;
}

function isPassageBeyondFrontier(
  player: Player,
  from: Coordinate,
  to: Coordinate,
  frontier: number | null,
): boolean {
  const leavesBoard = !isOnBoard(to);
  return (
    frontier !== null &&
    to.row === from.row + forwardRowDelta(player) &&
    Math.abs(to.column - from.column) <= 1 &&
    (!leavesBoard || to.column === from.column) &&
    progressAt(player, to) > frontier
  );
}

export function isWinningPassageTarget(
  state: GameState,
  player: Player,
  from: Coordinate,
  to: Coordinate,
): boolean {
  return isPassageBeyondFrontier(player, from, to, getFrontier(state, player));
}

function compareMoves(left: Move, right: Move): number {
  return (
    compareCoordinates(left.from, right.from) ||
    compareCoordinates(left.to, right.to) ||
    left.kind.localeCompare(right.kind)
  );
}

export function generateLegalMovesUnchecked(state: GameState): readonly Move[] {
  if (state.outcome !== null) return Object.freeze([]);

  const moves: Move[] = [];
  const frontier = getFrontier(state, state.activePlayer);
  for (let index = 0; index < state.cells.length; index += 1) {
    const source = state.cells[index];
    if (source?.kind !== "field" || source.stack.at(-1) !== state.activePlayer) continue;
    const from = coordinateAt(index);

    for (const offset of ADJACENT_OFFSETS) {
      const to = coordinate(from.row + offset.row, from.column + offset.column);
      if (isPassageBeyondFrontier(state.activePlayer, from, to, frontier)) {
        moves.push(Object.freeze({ kind: "passage", from, to }));
        continue;
      }

      if (!isOnBoard(to)) continue;
      const destination = state.cells[cellIndex(to)];
      if (destination?.kind === "field" && destination.stack.length < MAX_STACK_HEIGHT) {
        moves.push(Object.freeze({ kind: "normal", from, to }));
      }
    }
  }

  moves.sort(compareMoves);
  return Object.freeze(moves);
}

/** Rules-version 3 generation retained for persisted match compatibility. */
export function generateVersion3LegalMovesUnchecked(state: GameState): readonly Move[] {
  if (state.outcome !== null) return Object.freeze([]);

  const moves: Move[] = [];
  const currentFrontier = getFrontier(state, state.activePlayer);
  for (let index = 0; index < state.cells.length; index += 1) {
    const source = state.cells[index];
    if (source?.kind !== "field" || source.stack.at(-1) !== state.activePlayer) continue;
    const from = coordinateAt(index);
    const departureFrontier = getDepartureFrontier(
      state,
      state.activePlayer,
      from,
      currentFrontier,
    );

    for (const offset of ADJACENT_OFFSETS) {
      const to = coordinate(from.row + offset.row, from.column + offset.column);
      const passesCurrentFrontier = isPassageBeyondFrontier(
        state.activePlayer,
        from,
        to,
        currentFrontier,
      );
      const passesDepartureFrontier = isPassageBeyondFrontier(
        state.activePlayer,
        from,
        to,
        departureFrontier,
      );
      const destination = isOnBoard(to) ? state.cells[cellIndex(to)] : undefined;
      const validNormalDestination =
        destination?.kind === "field" && destination.stack.length < MAX_STACK_HEIGHT;
      if (passesCurrentFrontier || (passesDepartureFrontier && validNormalDestination)) {
        moves.push(Object.freeze({ kind: "passage", from, to }));
        continue;
      }

      if (!isOnBoard(to)) continue;
      if (validNormalDestination) moves.push(Object.freeze({ kind: "normal", from, to }));
    }
  }

  moves.sort(compareMoves);
  return Object.freeze(moves);
}

/** Deployed rules-version 2 generation retained for persisted match compatibility. */
export function generateVersion2LegalMovesUnchecked(state: GameState): readonly Move[] {
  if (state.outcome !== null) return Object.freeze([]);

  const moves: Move[] = [];
  const currentFrontier = getFrontier(state, state.activePlayer);
  for (let index = 0; index < state.cells.length; index += 1) {
    const source = state.cells[index];
    if (source?.kind !== "field" || source.stack.at(-1) !== state.activePlayer) continue;
    const from = coordinateAt(index);
    const departureFrontier = getDepartureFrontier(
      state,
      state.activePlayer,
      from,
      currentFrontier,
    );

    for (const offset of ADJACENT_OFFSETS) {
      const to = coordinate(from.row + offset.row, from.column + offset.column);
      if (isPassageBeyondFrontier(state.activePlayer, from, to, departureFrontier)) {
        moves.push(Object.freeze({ kind: "passage", from, to }));
        continue;
      }

      if (!isOnBoard(to)) continue;
      const destination = state.cells[cellIndex(to)];
      if (destination?.kind === "field" && destination.stack.length < MAX_STACK_HEIGHT) {
        moves.push(Object.freeze({ kind: "normal", from, to }));
      }
    }
  }

  moves.sort(compareMoves);
  return Object.freeze(moves);
}

/** Rules-version 1 move generation retained only for persisted replay compatibility. */
export function generateLegacyLegalMovesUnchecked(state: GameState): readonly Move[] {
  if (state.outcome !== null) return Object.freeze([]);

  const moves: Move[] = [];
  const frontier = getFrontier(state, state.activePlayer);
  for (let index = 0; index < state.cells.length; index += 1) {
    const source = state.cells[index];
    if (source?.kind !== "field" || source.stack.at(-1) !== state.activePlayer) continue;
    const from = coordinateAt(index);

    for (const offset of ADJACENT_OFFSETS) {
      const to = coordinate(from.row + offset.row, from.column + offset.column);
      if (isPassageBeyondFrontier(state.activePlayer, from, to, frontier)) {
        moves.push(Object.freeze({ kind: "passage", from, to }));
        continue;
      }

      if (!isOnBoard(to)) continue;
      const destination = state.cells[cellIndex(to)];
      if (destination?.kind === "field" && destination.stack.length < MAX_STACK_HEIGHT) {
        moves.push(Object.freeze({ kind: "normal", from, to }));
      }
    }
  }

  moves.sort(compareMoves);
  return Object.freeze(moves);
}

export function hasExistingNeighbor(cells: readonly Cell[], position: Coordinate): boolean {
  return ADJACENT_OFFSETS.some((offset) => {
    const neighbor = coordinate(position.row + offset.row, position.column + offset.column);
    return isOnBoard(neighbor) && cells[cellIndex(neighbor)]?.kind === "field";
  });
}

export function findOrphans(cells: readonly Cell[]): readonly RemovedOrphan[] {
  const orphans: RemovedOrphan[] = [];
  for (let index = 0; index < cells.length; index += 1) {
    const cell = cells[index];
    if (cell?.kind !== "field") continue;
    const position = coordinateAt(index);
    if (!hasExistingNeighbor(cells, position)) {
      orphans.push(
        Object.freeze({
          position,
          stack: Object.freeze([...cell.stack]),
        }),
      );
    }
  }
  orphans.sort((left, right) => compareCoordinates(left.position, right.position));
  return Object.freeze(orphans);
}

export function countDiscs(state: GameState, player: Player): number {
  return state.cells.reduce(
    (count, cell) =>
      count + (cell.kind === "field" ? cell.stack.filter((disc) => disc === player).length : 0),
    0,
  );
}

export function createInitialCells(): readonly Cell[] {
  return Object.freeze(
    Array.from({ length: BOARD_SIZE }, (_, row) =>
      Array.from({ length: BOARD_SIZE }, (_, column) => {
        void column;
        if (row === 0) return fieldCell(["red"]);
        if (row === BOARD_SIZE - 1) return fieldCell(["black"]);
        return fieldCell();
      }),
    ).flat(),
  );
}
